import { Injectable } from '@angular/core';
import * as Blockly from 'blockly';
import { javascriptGenerator } from 'blockly/javascript';
import { BlocklyStorageService } from 'src/app/shared/services/blockly-storage.service';
import { OpenaiAdminService } from 'src/app/shared/services/ai/text/openai-admin.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { BlockSearchEntry, BlockSearchService } from 'src/app/shared/services/block-search.service';
import { BLOCKLY_AI_PROMPT_PART1, BLOCKLY_AI_PROMPT_PART2 } from './blockly-ai-prompt';
import {
  BlocklyEditorAPI,
  BlocklyEditorWorkspaceAPI,
  BlocklyEditorBlocksAPI,
  BlocklyEditorToolboxAPI,
  BlocklyEditorStagesAPI,
  BlocklyEditorEventsAPI,
  BlocklyEditorSearchAPI,
  BlocklyEditorAiAPI,
  BlockInfo,
  CategoryInfo,
  StageInfo,
  BlocklyEditorEventType,
  BlocklyEditorEventData,
  BlocklyEditorEventCallback
} from './blockly-editor-api.types';

@Injectable({
  providedIn: 'root'
})
export class BlocklyEditorApiService {

  private workspace: Blockly.WorkspaceSvg | null = null;
  private listeners = new Map<string, { type: BlocklyEditorEventType | '*'; callback: BlocklyEditorEventCallback; once: boolean }>();
  private listenerIdCounter = 0;
  private changeListenerId: string | null = null;
  private lastEmittedCode = '';
  private toolboxMode: 'standard' | 'expert' = 'standard';
  private toolboxContent: Blockly.utils.toolbox.ToolboxItemInfo[] = [];
  private expertToolboxContent: Blockly.utils.toolbox.ToolboxItemInfo[] = [];
  private uniqueId = '';
  private saveCallback: (() => void) | null = null;
  private switchToolboxCallback: ((mode: 'standard' | 'expert') => void) | null = null;

  constructor(
    private blocklyStorage: BlocklyStorageService,
    private openaiService: OpenaiAdminService,
    private rss: RouteShareService,
    private blockSearch: BlockSearchService
  ) { }

  private _extractBlockTypesFromToolbox(
    items: Blockly.utils.toolbox.ToolboxItemInfo[],
    categoryPath: string[] = [],
    result: { category: string; types: string[] }[] = [],
    skipCategories: string[] = ['Veraltete Blöcke']
  ): { category: string; types: string[] }[] {
    for (const item of items) {
      const cat = item as any;
      if (cat.kind === 'category') {
        const catName: string = cat.name ?? '';
        if (skipCategories.includes(catName)) continue;
        const newPath = categoryPath.length > 0 ? [...categoryPath, catName] : [catName];
        const blocks: string[] = (cat.contents ?? [])
          .filter((c: any) => c.kind === 'block' && c.type)
          .map((c: any) => c.type as string);
        if (blocks.length > 0) {
          result.push({ category: newPath.join(' > '), types: blocks });
        }
        this._extractBlockTypesFromToolbox(cat.contents ?? [], newPath, result, skipCategories);
      }
    }
    return result;
  }

  private _buildDynamicBlockList(): string {
    const allGroups = this._extractBlockTypesFromToolbox(this.toolboxContent);
    if (allGroups.length === 0) return '(Toolbox nicht geladen)';

    const blockIndex = new Map<string, BlockSearchEntry>();
    for (const entry of this.blockSearch['blocks'] as BlockSearchEntry[]) {
      blockIndex.set(entry.type, entry);
    }

    const lines: string[] = ['## VERFÜGBARE BLOCK-TYPEN (aus aktueller Toolbox)\n'];
    for (const { category, types } of allGroups) {
      if (types.length === 0) continue;
      lines.push(`### ${category}`);
      for (const type of types) {
        const entry = blockIndex.get(type);
        if (!entry) {
          lines.push(`- ${type}`);
          continue;
        }
        const json = entry.json;
        const label = json.message0
          .replace(/%\d+/g, '…')
          .replace(/[\u3164\n]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        lines.push(`- **${type}**  →  ${label}`);

        // Verbindungstyp
        if (json.hat === 'cap') {
          lines.push(`  Typ: Start-Block (steht immer oben, kein Vorgänger)`);
        } else if (json.output !== undefined && json.output !== null) {
          lines.push(`  Typ: Output (${json.output || 'beliebig'}) → muss als <value name="…"> eingebettet werden`);
        } else if (json.output === null) {
          lines.push(`  Typ: Output (beliebig) → muss als <value name="…"> eingebettet werden`);
        } else {
          lines.push(`  Typ: Aktion → verkettbar via <next> / <previous>`);
        }

        // Felder und Eingaben aus args0
        const args0 = (json.args0 ?? []) as any[];
        for (const arg of args0) {
          if (!arg.name || arg.name === 'ICON') continue;
          if (arg.type === 'input_value') {
            const check = Array.isArray(arg.check)
              ? arg.check.join('|')
              : (arg.check || 'beliebig');
            lines.push(`  <value name="${arg.name}"> (check: ${check})`);
          } else if (arg.type === 'input_statement') {
            const check = Array.isArray(arg.check)
              ? arg.check.join('|')
              : (arg.check || 'beliebig');
            lines.push(`  <statement name="${arg.name}"> (check: ${check})`);
          } else if (arg.type === 'field_dropdown' && arg.options) {
            const opts = (arg.options as [string, string][]).map(o => o[1]).slice(0, 6).join(', ');
            lines.push(`  <field name="${arg.name}"> (dropdown: ${opts}${arg.options.length > 6 ? ', …' : ''})`);
          } else if (['field_input', 'field_number', 'field_checkbox', 'field_multiline_modal', 'field_json_modal'].includes(arg.type)) {
            lines.push(`  <field name="${arg.name}"> (${arg.type.replace('field_', '')})`);
          }
        }
      }
      lines.push('');
    }
    return lines.join('\n');
  }

  private _truncateForPrompt(value: string, maxLength = 2000): string {
    if (!value) return '';
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength)}\n...[gekürzt]`;
  }

  private _buildBotContextForExplain(): string {
    const botConfig = this.rss.botConfig;
    if (!botConfig) {
      return 'BOT-KONTEXT: Keine Bot-Konfiguration verfügbar.';
    }

    const enabledTools = botConfig.toolConfigs
      .filter(toolConfig => toolConfig.enabled)
      .map(toolConfig => {
        const skillId = toolConfig.skillId?.trim() ? ` (skillId: ${toolConfig.skillId})` : '';
        return `- ${toolConfig.tool}${skillId}`;
      });

    const availableSkills = this.rss._allSkills()
      .filter(skill => skill && skill.trim() !== '');

    const botInstruction = this._truncateForPrompt(botConfig.botInstruction ?? '');

    return `BOT-KONTEXT:
Bot-Name: ${botConfig.name}
Bot-ID: ${botConfig._id}

SYSTEMPROMPT (botInstruction):
${botInstruction || '[leer]'}

AKTIVIERTE TOOLS:
${enabledTools.length > 0 ? enabledTools.join('\n') : '- Keine aktivierten Tools'}

VERFÜGBARE SKILLS (Skill-IDs):
${availableSkills.length > 0 ? availableSkills.join(', ') : 'Keine Skills verfügbar'}
`;
  }

  /**
   * Aktiviert die API mit dem aktuellen Workspace.
   * Gibt das API-Objekt zurück, das auf window gesetzt wird.
   */
  public activate(
    workspace: Blockly.WorkspaceSvg,
    uniqueId: string,
    toolboxContent: Blockly.utils.toolbox.ToolboxItemInfo[],
    expertToolboxContent: Blockly.utils.toolbox.ToolboxItemInfo[],
    toolboxMode: 'standard' | 'expert',
    saveCallback: () => void,
    switchToolboxCallback: (mode: 'standard' | 'expert') => void
  ): BlocklyEditorAPI {
    this.workspace = workspace;
    this.uniqueId = uniqueId;
    this.toolboxContent = toolboxContent;
    this.expertToolboxContent = expertToolboxContent;
    this.toolboxMode = toolboxMode;
    this.saveCallback = saveCallback;
    this.switchToolboxCallback = switchToolboxCallback;
    this.lastEmittedCode = '';

    // Workspace change listener for event dispatching
    this.changeListenerId = workspace.addChangeListener((event: Blockly.Events.Abstract) => {
      this._dispatchEvent(event);
    }) as unknown as string;

    return this._buildApi();
  }

  /**
   * Deaktiviert die API und räumt auf.
   */
  public deactivate(): void {
    this.listeners.clear();
    this.workspace = null;
    this.saveCallback = null;
    this.switchToolboxCallback = null;
  }

  public updateContext(
    uniqueId: string,
    toolboxContent: Blockly.utils.toolbox.ToolboxItemInfo[],
    expertToolboxContent: Blockly.utils.toolbox.ToolboxItemInfo[],
    toolboxMode: 'standard' | 'expert'
  ) {
    this.uniqueId = uniqueId;
    this.toolboxContent = toolboxContent;
    this.expertToolboxContent = expertToolboxContent;
    this.toolboxMode = toolboxMode;
  }

  // ─────────────────────────────────────────────────────────
  // PRIVATE HELPERS
  // ─────────────────────────────────────────────────────────

  private _getWorkspace(): Blockly.WorkspaceSvg {
    if (!this.workspace) throw new Error('BLOCKLY_EDITOR: Editor ist nicht aktiv.');
    return this.workspace;
  }

  private _blockToInfo(block: Blockly.Block): BlockInfo {
    const fields: Record<string, any> = {};
    block.inputList.forEach(input => {
      input.fieldRow.forEach(field => {
        if (field.name) {
          fields[field.name] = field.getValue();
        }
      });
    });

    const children: BlockInfo[] = [];
    block.getChildren(true).forEach(child => {
      children.push(this._blockToInfo(child));
    });

    const inputNames = block.inputList.map(i => i.name).filter(Boolean);

    return {
      id: block.id,
      type: block.type,
      x: block.getRelativeToSurfaceXY().x,
      y: block.getRelativeToSurfaceXY().y,
      disabled: !block.isEnabled(),
      comment: block.getCommentText(),
      fields,
      children,
      parentId: block.getParent()?.id ?? null,
      inputNames,
      outputType: block.outputConnection?.getCheck()?.[0] ?? null,
      collapsed: block.isCollapsed()
    };
  }

  private _dispatchEvent(event: Blockly.Events.Abstract): void {
    let eventType: BlocklyEditorEventType | null = null;
    const data: Partial<BlocklyEditorEventData> = {};

    switch (event.type) {
      case Blockly.Events.BLOCK_CREATE:
        eventType = 'block_create';
        data.blockId = (event as any).blockId;
        break;
      case Blockly.Events.BLOCK_DELETE:
        eventType = 'block_delete';
        data.blockId = (event as any).blockId;
        break;
      case Blockly.Events.BLOCK_MOVE:
        eventType = 'block_move';
        data.blockId = (event as any).blockId;
        break;
      case Blockly.Events.BLOCK_CHANGE:
        eventType = 'block_change';
        data.blockId = (event as any).blockId;
        data.oldValue = (event as any).oldValue;
        data.newValue = (event as any).newValue;
        break;
      default:
        eventType = 'workspace_change';
        break;
    }

    // Code-Change erkennen
    const ws = this._getWorkspace();
    const newCode = javascriptGenerator.workspaceToCode(ws);
    if (newCode !== this.lastEmittedCode) {
      this.lastEmittedCode = newCode;
      this._notifyListeners({ type: 'code_change', code: newCode } as BlocklyEditorEventData);
    }

    if (eventType) {
      data.type = eventType;
      this._notifyListeners(data as BlocklyEditorEventData);
    }
  }

  private _notifyListeners(event: BlocklyEditorEventData): void {
    const toRemove: string[] = [];
    this.listeners.forEach((listener, id) => {
      if (listener.type === '*' || listener.type === event.type) {
        listener.callback(event);
        if (listener.once) toRemove.push(id);
      }
    });
    toRemove.forEach(id => this.listeners.delete(id));
  }

  private _generateListenerId(): string {
    return `blockly_listener_${++this.listenerIdCounter}`;
  }

  // ─────────────────────────────────────────────────────────
  // BUILD API
  // ─────────────────────────────────────────────────────────

  private _buildApi(): BlocklyEditorAPI {
    const self = this;

    const workspaceApi: BlocklyEditorWorkspaceAPI = {
      getXml(): string {
        const ws = self._getWorkspace();
        const xml = Blockly.Xml.workspaceToDom(ws);
        return Blockly.Xml.domToText(xml);
      },
      getJson(): object {
        const ws = self._getWorkspace();
        return Blockly.serialization.workspaces.save(ws);
      },
      getCode(): string {
        const ws = self._getWorkspace();
        return javascriptGenerator.workspaceToCode(ws);
      },
      getBlocks(): BlockInfo[] {
        const ws = self._getWorkspace();
        return ws.getTopBlocks(true).map(b => self._blockToInfo(b));
      },
      getBlockById(id: string): BlockInfo | null {
        const ws = self._getWorkspace();
        const block = ws.getBlockById(id);
        return block ? self._blockToInfo(block) : null;
      },
      getBlocksByType(type: string): BlockInfo[] {
        const ws = self._getWorkspace();
        return ws.getBlocksByType(type, true).map(b => self._blockToInfo(b));
      },
      getBlockCount(): number {
        const ws = self._getWorkspace();
        return ws.getAllBlocks(false).length;
      },
      isDirty(): boolean {
        const ws = self._getWorkspace();
        const currentXml = Blockly.Xml.domToText(Blockly.Xml.workspaceToDom(ws));
        const storedXml = self.blocklyStorage.getStageXmlByUniqueId(self.uniqueId);
        return currentXml !== storedXml;
      },
      getUniqueId(): string {
        return self.uniqueId;
      },
      loadXml(xmlString: string): void {
        const ws = self._getWorkspace();
        const xml = Blockly.utils.xml.textToDom(xmlString);
        // @ts-ignore
        Blockly.Xml.clearWorkspaceAndLoadFromXml(xml, ws);
      },
      loadJson(json: object): void {
        const ws = self._getWorkspace();
        Blockly.serialization.workspaces.load(json, ws);
      },
      clear(): void {
        const ws = self._getWorkspace();
        ws.clear();
        ws.clearUndo();
      },
      async save(): Promise<void> {
        self.saveCallback?.();
      },
      undo(): void {
        const ws = self._getWorkspace();
        ws.undo(false);
      },
      redo(): void {
        const ws = self._getWorkspace();
        ws.undo(true);
      },
      zoomToFit(): void {
        const ws = self._getWorkspace();
        ws.zoomToFit();
      },
      scrollToBlock(blockId: string): void {
        const ws = self._getWorkspace();
        const block = ws.getBlockById(blockId);
        if (block) {
          ws.centerOnBlock(blockId);
          block.select();
        }
      },
      resize(): void {
        const ws = self._getWorkspace();
        Blockly.svgResize(ws);
      }
    };

    const blocksApi: BlocklyEditorBlocksAPI = {
      add(type: string, x = 50, y = 50): string | null {
        const ws = self._getWorkspace();
        const block = ws.newBlock(type);
        block.initSvg();
        block.render();
        block.moveBy(x, y);
        return block.id;
      },
      addFromXml(xmlString: string): string[] {
        const ws = self._getWorkspace();
        const xml = Blockly.utils.xml.textToDom(xmlString);
        const ids: string[] = [];
        const blockElements = xml.querySelectorAll(':scope > block');
        blockElements.forEach((blockEl) => {
          const dom = Blockly.utils.xml.textToDom(
            new XMLSerializer().serializeToString(blockEl)
          );
          // @ts-ignore
          const block = Blockly.Xml.domToBlock(dom, ws);
          ids.push(block.id);
        });
        // If the xml itself is a single <block>, handle that too
        if (blockElements.length === 0 && xml.tagName?.toLowerCase() === 'block') {
          // @ts-ignore
          const block = Blockly.Xml.domToBlock(xml, ws);
          ids.push(block.id);
        }
        return ids;
      },
      remove(blockId: string): boolean {
        const ws = self._getWorkspace();
        const block = ws.getBlockById(blockId);
        if (!block) return false;
        block.dispose(true);
        return true;
      },
      removeAll(): void {
        const ws = self._getWorkspace();
        ws.clear();
      },
      connect(sourceBlockId: string, inputName: string, targetBlockId: string): boolean {
        const ws = self._getWorkspace();
        const source = ws.getBlockById(sourceBlockId);
        const target = ws.getBlockById(targetBlockId);
        if (!source || !target) return false;
        const input = source.getInput(inputName);
        if (!input || !input.connection) return false;
        const targetConn = target.outputConnection || target.previousConnection;
        if (!targetConn) return false;
        input.connection.connect(targetConn);
        return true;
      },
      disconnect(blockId: string): boolean {
        const ws = self._getWorkspace();
        const block = ws.getBlockById(blockId);
        if (!block) return false;
        if (block.outputConnection?.isConnected()) {
          block.outputConnection.disconnect();
          return true;
        }
        if (block.previousConnection?.isConnected()) {
          block.previousConnection.disconnect();
          return true;
        }
        return false;
      },
      setField(blockId: string, fieldName: string, value: any): boolean {
        const ws = self._getWorkspace();
        const block = ws.getBlockById(blockId);
        if (!block) return false;
        const field = block.getField(fieldName);
        if (!field) return false;
        field.setValue(value);
        return true;
      },
      getField(blockId: string, fieldName: string): any {
        const ws = self._getWorkspace();
        const block = ws.getBlockById(blockId);
        if (!block) return undefined;
        const field = block.getField(fieldName);
        return field?.getValue();
      },
      duplicate(blockId: string): string | null {
        const ws = self._getWorkspace();
        const block = ws.getBlockById(blockId);
        if (!block) return null;
        const xml = Blockly.Xml.blockToDom(block);
        // @ts-ignore
        const newBlock = Blockly.Xml.domToBlock(xml, ws);
        newBlock.moveBy(20, 20);
        return newBlock.id;
      },
      moveToCenter(blockId: string): void {
        const ws = self._getWorkspace();
        ws.centerOnBlock(blockId);
      }
    };

    const toolboxApi: BlocklyEditorToolboxAPI = {
      getCategories(): CategoryInfo[] {
        const contents = self.toolboxMode === 'expert' ? self.expertToolboxContent : self.toolboxContent;
        return contents
          .filter((item: any) => item.kind === 'category')
          .map((item: any) => ({
            name: item.name || '',
            colour: item.colour || '',
            blockTypes: (item.contents || [])
              .filter((c: any) => c.kind === 'block')
              .map((c: any) => c.type)
          }));
      },
      getBlockTypes(): string[] {
        const ws = self._getWorkspace();
        // @ts-ignore - Blockly internals
        return Object.keys(Blockly.Blocks);
      },
      getMode(): 'standard' | 'expert' {
        return self.toolboxMode;
      },
      switchMode(mode: 'standard' | 'expert'): void {
        self.toolboxMode = mode;
        self.switchToolboxCallback?.(mode);
      },
      addCategory(name: string, blocks: Array<{ type: string }>, colour?: string): void {
        const ws = self._getWorkspace();
        const newCategory: any = {
          kind: 'category',
          name,
          colour: colour || '#999',
          contents: blocks.map(b => ({ kind: 'block', type: b.type }))
        };
        self.toolboxContent.push(newCategory);
        // @ts-ignore
        ws.updateToolbox({ kind: 'categoryToolbox', contents: self.toolboxContent });
      },
      removeCategory(name: string): void {
        const ws = self._getWorkspace();
        self.toolboxContent = self.toolboxContent.filter((item: any) => item.name !== name);
        // @ts-ignore
        ws.updateToolbox({ kind: 'categoryToolbox', contents: self.toolboxContent });
      },
      refresh(): void {
        const ws = self._getWorkspace();
        const contents = self.toolboxMode === 'expert' ? self.expertToolboxContent : self.toolboxContent;
        // @ts-ignore
        ws.updateToolbox({ kind: 'categoryToolbox', contents });
      }
    };

    const stagesApi: BlocklyEditorStagesAPI = {
      list(): StageInfo[] {
        return self.blocklyStorage.stages.map(s => ({
          uniqueId: s.uniqueId,
          xml: s.xml,
          updatedBy: s.updatedBy,
          updated: s.updated
        }));
      },
      getXml(uniqueId: string): string {
        return self.blocklyStorage.getStageXmlByUniqueId(uniqueId);
      },
      getCode(uniqueId: string): string {
        const xml = self.blocklyStorage.getStageXmlByUniqueId(uniqueId);
        // Parse XML in temporärem Workspace, generiere Code
        const tempWs = new Blockly.Workspace();
        try {
          const dom = Blockly.utils.xml.textToDom(xml);
          // @ts-ignore
          Blockly.Xml.clearWorkspaceAndLoadFromXml(dom, tempWs);
          return javascriptGenerator.workspaceToCode(tempWs);
        } finally {
          tempWs.dispose();
        }
      },
      async create(uniqueId: string, xml?: string): Promise<void> {
        const finalXml = xml || self.blocklyStorage.getEmptyXmlFor(uniqueId);
        await self.blocklyStorage.createStage(uniqueId, finalXml);
      },
      async update(uniqueId: string, xml: string): Promise<void> {
        await self.blocklyStorage.updateStage(uniqueId, xml, '');
      },
      async delete(uniqueId: string): Promise<void> {
        await self.blocklyStorage.deleteStage(uniqueId);
      },
      load(uniqueId: string): void {
        const xml = self.blocklyStorage.getStageXmlByUniqueId(uniqueId);
        workspaceApi.loadXml(xml);
      }
    };

    const eventsApi: BlocklyEditorEventsAPI = {
      on(eventType: BlocklyEditorEventType | '*', callback: BlocklyEditorEventCallback): string {
        const id = self._generateListenerId();
        self.listeners.set(id, { type: eventType, callback, once: false });
        return id;
      },
      off(listenerId: string): void {
        self.listeners.delete(listenerId);
      },
      once(eventType: BlocklyEditorEventType | '*', callback: BlocklyEditorEventCallback): string {
        const id = self._generateListenerId();
        self.listeners.set(id, { type: eventType, callback, once: true });
        return id;
      }
    };

    const searchApi: BlocklyEditorSearchAPI = {
      byText(term: string): BlockInfo[] {
        const ws = self._getWorkspace();
        const allBlocks = ws.getAllBlocks(false);
        const lower = term.toLowerCase();
        return allBlocks
          .filter(block => {
            for (const input of block.inputList) {
              for (const field of input.fieldRow) {
                const val = field.getValue();
                if (val && String(val).toLowerCase().includes(lower)) return true;
              }
            }
            return false;
          })
          .map(b => self._blockToInfo(b));
      },
      byType(blockType: string): BlockInfo[] {
        const ws = self._getWorkspace();
        return ws.getBlocksByType(blockType, false).map(b => self._blockToInfo(b));
      },
      highlight(blockId: string): void {
        const ws = self._getWorkspace();
        const block = ws.getBlockById(blockId);
        if (block) {
          block.setHighlighted(true);
          block.select();
        }
      },
      clearHighlights(): void {
        const ws = self._getWorkspace();
        ws.getAllBlocks(false).forEach(block => {
          block.setHighlighted(false);
        });
      }
    };

    const aiApi: BlocklyEditorAiAPI = {
      async fromUseCase(description: string): Promise<string> {
        const currentXml = workspaceApi.getXml();
        const dynamicBlockList = self._buildDynamicBlockList();

        const availableSkills = self.rss.botConfig
          ? self.rss._allSkills().filter((s: string) => s?.trim())
          : [];
        const skillContext = availableSkills.length > 0
          ? `## VERFÜGBARE SKILL-IDs (für Skill-Blöcke)\n${availableSkills.map((s: string) => `- ${s}`).join('\n')}\n`
          : '';

        const prompt = `${BLOCKLY_AI_PROMPT_PART1}${dynamicBlockList}

${skillContext}${BLOCKLY_AI_PROMPT_PART2}

---
AKTUELLER WORKSPACE (Kontext):
${currentXml}

---
USE CASE (Benutzeranforderung):
${description}

---
Generiere NUR gültiges Blockly XML (<xml>...</xml>) für diesen Use Case. Keine Erklärungen, nur XML.`;

        const result = await self.openaiService.chatCompletion(prompt, 'gpt-4.1', 0.3);
        // Extrahiere XML aus der Antwort
        let xmlResult = String(result);
        const xmlMatch = xmlResult.match(/<xml[\s\S]*?<\/xml>/);
        if (xmlMatch) xmlResult = xmlMatch[0];
        return xmlResult;
      },
      async fromUseCaseAndApply(description: string): Promise<string[]> {
        const xml = await aiApi.fromUseCase(description);
        return blocksApi.addFromXml(xml);
      },
      async explain(): Promise<string> {
        const code = workspaceApi.getCode();
        const xml = workspaceApi.getXml();
        const botContext = self._buildBotContextForExplain();
        const prompt = `Du bist ein Experte für den RAGAI Blockly-Editor. Erkläre was folgender Blockly-Workspace macht.

${botContext}

GENERIERTER CODE:
\`\`\`javascript
${code}
\`\`\`

XML DES WORKSPACE:
${xml}

Erkläre in 2-5 Sätzen auf Deutsch was dieser Workspace bewirkt. Beziehe den Bot-Kontext (Systemprompt, Tools und Skills) in die Erklärung ein. Sei konkret und nenne die genutzten RAGAI-Funktionen.`;

        const result = await self.openaiService.chatCompletion(prompt, 'gpt-4.1', 0.3);
        return String(result);
      }
    };

    const api: BlocklyEditorAPI = {
      version: '1.0.0',
      isActive: true,
      workspace: workspaceApi,
      blocks: blocksApi,
      toolbox: toolboxApi,
      stages: stagesApi,
      events: eventsApi,
      search: searchApi,
      ai: aiApi,
      help(): void {
        const helpText = `
╔══════════════════════════════════════════════════════════════╗
║              window.BLOCKLY_EDITOR API v1.0.0               ║
╠══════════════════════════════════════════════════════════════╣
║                                                              ║
║  📖 WORKSPACE (Lesen & Steuern)                             ║
║  ─────────────────────────────────────────────────────────── ║
║  .workspace.getXml()          → XML des Workspace            ║
║  .workspace.getJson()         → JSON-Serialisierung          ║
║  .workspace.getCode()         → Generierter JavaScript-Code  ║
║  .workspace.getBlocks()       → Alle Top-Level-Blöcke        ║
║  .workspace.getBlockById(id)  → Block nach ID                ║
║  .workspace.getBlocksByType(t)→ Blöcke nach Typ              ║
║  .workspace.getBlockCount()   → Anzahl aller Blöcke          ║
║  .workspace.isDirty()         → Ungespeicherte Änderungen?   ║
║  .workspace.getUniqueId()     → Aktueller Stage-Identifier   ║
║  .workspace.loadXml(xml)      → XML laden                    ║
║  .workspace.loadJson(json)    → JSON laden                   ║
║  .workspace.clear()           → Workspace leeren             ║
║  .workspace.save()            → Speichern (async)            ║
║  .workspace.undo()            → Rückgängig                   ║
║  .workspace.redo()            → Wiederherstellen             ║
║  .workspace.zoomToFit()       → Zoom auf alle Blöcke         ║
║  .workspace.scrollToBlock(id) → Zu Block scrollen            ║
║  .workspace.resize()          → Workspace-Größe anpassen     ║
║                                                              ║
║  🧱 BLOCKS (Blöcke manipulieren)                             ║
║  ─────────────────────────────────────────────────────────── ║
║  .blocks.add(type, x?, y?)    → Block hinzufügen → ID        ║
║  .blocks.addFromXml(xml)      → Blöcke aus XML → IDs         ║
║  .blocks.remove(id)           → Block entfernen              ║
║  .blocks.removeAll()          → Alle Blöcke entfernen        ║
║  .blocks.connect(src,inp,tgt) → Blöcke verbinden            ║
║  .blocks.disconnect(id)       → Block trennen               ║
║  .blocks.setField(id,name,val)→ Feld-Wert setzen            ║
║  .blocks.getField(id,name)    → Feld-Wert lesen             ║
║  .blocks.duplicate(id)        → Block duplizieren → ID       ║
║  .blocks.moveToCenter(id)     → Block zentrieren             ║
║                                                              ║
║  🧰 TOOLBOX                                                  ║
║  ─────────────────────────────────────────────────────────── ║
║  .toolbox.getCategories()     → Kategorie-Liste              ║
║  .toolbox.getBlockTypes()     → Alle registrierten Typen     ║
║  .toolbox.getMode()           → 'standard' | 'expert'        ║
║  .toolbox.switchMode(mode)    → Modus wechseln              ║
║  .toolbox.addCategory(name,b) → Kategorie hinzufügen        ║
║  .toolbox.removeCategory(name)→ Kategorie entfernen          ║
║  .toolbox.refresh()           → Toolbox aktualisieren        ║
║                                                              ║
║  📋 STAGES                                                   ║
║  ─────────────────────────────────────────────────────────── ║
║  .stages.list()               → Alle gespeicherten Stages    ║
║  .stages.getXml(uniqueId)     → XML einer Stage              ║
║  .stages.getCode(uniqueId)    → Code einer Stage             ║
║  .stages.create(id, xml?)     → Stage erstellen (async)      ║
║  .stages.update(id, xml)      → Stage aktualisieren (async)  ║
║  .stages.delete(id)           → Stage löschen (async)        ║
║  .stages.load(uniqueId)       → Stage in Editor laden        ║
║                                                              ║
║  📡 EVENTS                                                   ║
║  ─────────────────────────────────────────────────────────── ║
║  .events.on(type, callback)   → Listener registrieren → ID   ║
║  .events.off(listenerId)      → Listener entfernen           ║
║  .events.once(type, callback) → Einmal-Listener → ID         ║
║  Types: block_create, block_delete, block_move,              ║
║         block_change, workspace_change, code_change, save    ║
║                                                              ║
║  🔍 SEARCH                                                   ║
║  ─────────────────────────────────────────────────────────── ║
║  .search.byText(term)         → Blöcke mit Text-Match        ║
║  .search.byType(type)         → Blöcke nach Typ              ║
║  .search.highlight(id)        → Block hervorheben            ║
║  .search.clearHighlights()    → Hervorhebung entfernen       ║
║                                                              ║
║  🤖 AI                                                       ║
║  ─────────────────────────────────────────────────────────── ║
║  .ai.fromUseCase(desc)        → XML generieren (async)       ║
║  .ai.fromUseCaseAndApply(desc)→ Generieren + einfügen        ║
║  .ai.explain()                → Workspace erklären (async)   ║
║                                                              ║
╚══════════════════════════════════════════════════════════════╝
`;
        console.log(helpText);
        console.table([
          { Kategorie: 'workspace', Methoden: 19 },
          { Kategorie: 'blocks', Methoden: 10 },
          { Kategorie: 'toolbox', Methoden: 7 },
          { Kategorie: 'stages', Methoden: 6 },
          { Kategorie: 'events', Methoden: 3 },
          { Kategorie: 'search', Methoden: 4 },
          { Kategorie: 'ai', Methoden: 3 }
        ]);
      }
    };

    return api;
  }
}
